// Interactive attract overlay — the runtime half of the "Stop Screen" feature.
//
// Sits ON TOP of the normal ContentDisplay. In the ATTRACT state it covers the
// screen with its own image/video rotation (reusing ContentDisplay over the
// stop screen's items) plus an optional "Touch to preview" prompt. A touch
// dismisses it (REVEALED) — the App pauses the underlying content while covered
// and resumes it here — and after `dwell_seconds` of no touch it re-covers.
//
// Everything is local (touch + timer), so it works fully offline: the config
// and items ride in the cached manifest, and its media warms into cache on the
// first attract cycle. If there are no items it renders nothing (the App then
// never treats the content as covered), so the screen is never blacked out.

import { useCallback, useEffect, useRef } from 'react'
import type { ContentResponse, StopScreenManifest } from '../lib/deviceApi'
import ContentDisplay from './ContentDisplay'

// CTA position → absolute-position classes (matches the dashboard's 3×3 grid).
const CTA_POS_CLASS: Record<string, string> = {
  'top-left': 'top-[6%] start-[6%]',
  'top-center': 'top-[6%] start-1/2 -translate-x-1/2',
  'top-right': 'top-[6%] end-[6%]',
  'center-left': 'top-1/2 -translate-y-1/2 start-[6%]',
  'center': 'top-1/2 start-1/2 -translate-x-1/2 -translate-y-1/2',
  'center-right': 'top-1/2 -translate-y-1/2 end-[6%]',
  'bottom-left': 'bottom-[6%] start-[6%]',
  'bottom-center': 'bottom-[6%] start-1/2 -translate-x-1/2',
  'bottom-right': 'bottom-[6%] end-[6%]',
}

interface Props {
  baseResponse:   ContentResponse   // for scaling / audio settings on the attract rotation
  stopScreen:     StopScreenManifest
  orientationType: 'horizontal' | 'vertical'
  isOnline:       boolean | null
  audioUnlocked?: boolean
  covered:        boolean            // true = attract showing; false = revealed
  onDismiss:      () => void         // touch dismissed the overlay
  onRestartDwell: () => void         // touch while revealed → restart the dwell timer
  onError:        (msg: string) => void
  onRetry:        (itemName: string) => void
}

export default function StopScreenOverlay({
  baseResponse, stopScreen, orientationType, isOnline, audioUnlocked = false,
  covered, onDismiss, onRestartDwell, onError, onRetry,
}: Props) {
  const items = stopScreen.items ?? []

  // While revealed, any touch anywhere restarts the dwell countdown so an
  // actively-interacting customer isn't interrupted. Passive doc listener — it
  // never blocks interaction with the revealed content beneath.
  useEffect(() => {
    if (covered) return
    const onDown = () => onRestartDwell()
    document.addEventListener('pointerdown', onDown, { passive: true })
    return () => document.removeEventListener('pointerdown', onDown)
  }, [covered, onRestartDwell])

  const dismiss = useCallback(() => { if (covered) onDismiss() }, [covered, onDismiss])

  // Nothing to attract with → render nothing (App won't mark content covered).
  if (items.length === 0) return null

  // Synthesize a response so ContentDisplay renders JUST the attract items
  // (no layout / no nested stop screen), inheriting scaling + audio settings.
  const attractResponse: ContentResponse = {
    ...baseResponse,
    items,
    layout: null,
    stop_screen: null,
  }

  const ctaClass = CTA_POS_CLASS[stopScreen.cta_position] ?? CTA_POS_CLASS.center

  // Opaque black backdrop BEHIND the attract Carousel below, not just
  // decoration: the crossfade/slide/zoom transitions between attract items
  // move or fade their own tiles mid-animation (see ContentDisplay's
  // tr-* classes), and this container had no background of its own — so for
  // the length of every transition, the real underlying screen content (the
  // actual ContentDisplay this overlay is covering) showed through the gap
  // instead of a neutral backdrop. Literal black (not bg-bg's #0A0A0A) to
  // match every other opaque letterbox/backdrop in the player (ImageDisplay,
  // VideoDisplay) exactly, with zero tonal seam between them — and Android's
  // StopScreenLayer, which uses Color.Black for the same reason.
  return (
    <div
      className="absolute inset-0 z-20 bg-black"
      style={{
        visibility: covered ? 'visible' : 'hidden',
        pointerEvents: covered ? 'auto' : 'none',
        // Lets the CTA button's cqmin font-size (see CtaButton/SIZE_CQMIN)
        // scale relative to THIS box — which fills the stage exactly, so it
        // tracks the physical screen frame correctly even when the stage is
        // rotated/letterboxed, matching the dashboard preview's own frame-
        // relative scaling.
        containerType: 'size',
      } as React.CSSProperties}
      aria-hidden={!covered}
    >
      {/* Attract rotation — paused (frozen) while revealed so it isn't decoding
          a hidden video; resumes when covered again. */}
      <ContentDisplay
        response={attractResponse}
        orientationType={orientationType}
        isOnline={isOnline}
        audioUnlocked={audioUnlocked}
        paused={!covered}
        onError={onError}
        onRetry={onRetry}
        onItemAdvanced={() => { /* attract has no pending-content staging */ }}
      />

      {/* Full-area touch catcher for 'anywhere' mode (sits under the CTA). */}
      {stopScreen.touch_mode === 'anywhere' && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismiss}
          className="absolute inset-0"
          style={{ zIndex: 0 }}
        />
      )}

      {/* CTA prompt. In button_only mode it's the sole dismiss target; in
          anywhere mode it's a hint that also dismisses. Its shape / fill /
          colours / size are operator-customizable (ride in the manifest). */}
      {stopScreen.show_cta && (
        <CtaButton stopScreen={stopScreen} posClass={ctaClass} onClick={dismiss} />
      )}
    </div>
  )
}

// ── CTA button ───────────────────────────────────────────────────────────────
// Renders the dismiss prompt from the manifest's cta_* appearance fields.

// #RGB / #RRGGBB / #RRGGBBAA → {r,g,b} (alpha ignored for derived colours).
function hexRgb(hex: string): { r: number; g: number; b: number } {
  let h = (hex || '').replace('#', '')
  if (h.length === 3) h = h.split('').map(c => c + c).join('')
  if (h.length === 8) h = h.slice(0, 6)
  const n = parseInt(h || 'ffffff', 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}
const rgba = (hex: string, a: number) => { const { r, g, b } = hexRgb(hex); return `rgba(${r},${g},${b},${a})` }
const darken = (hex: string, amt: number) => {
  const { r, g, b } = hexRgb(hex); const f = (v: number) => Math.round(v * (1 - amt))
  return `rgb(${f(r)},${f(g)},${f(b)})`
}

// Font size in container-query-min units, scaled to the STAGE (the physical
// screen frame — see the containerType:'size' on this file's root div), NOT
// fixed px. Fixed px sizes here made the button's on-screen size depend on
// the device's raw resolution (tiny on a 4K panel, comparatively huge on a
// 720p one) while the dashboard's preview always scaled proportionally to
// its own frame — the two could only ever agree by coincidence at one
// specific resolution. This is the EXACT scale StopScreenDetailsPage.tsx's
// CTA_PREVIEW_FONT uses, so the preview is now an accurate prediction of the
// real button's size on any screen. Padding/gap on the button are in em, so
// they follow the same scale automatically.
const SIZE_CQMIN: Record<string, string> = {
  xs: '2.4cqmin',
  sm: '3.0cqmin',
  md: '3.6cqmin',
  lg: '4.4cqmin',
  xl: '5.4cqmin',
}

function ctaFillStyle(style: string, bg: string, fg: string): React.CSSProperties {
  switch (style) {
    case 'solid':    return { background: bg, color: fg }
    case 'gradient': return { background: `linear-gradient(135deg, ${bg}, ${darken(bg, 0.4)})`, color: fg }
    case 'outline':  return { background: 'transparent', color: fg, border: `2px solid ${bg}` }
    case 'glassy':
    default:
      // Genuinely glassy: low fill opacity + a strong blur so the attract
      // rotation behind actually shows through (frosted), plus a light edge
      // highlight — the standard glassmorphism cue. The old 0.85 opacity /
      // 8px blur was so close to opaque the blur was barely perceptible.
      // Must match StopScreenDetailsPage.tsx's ctaFillStyle exactly so the
      // dashboard preview and the live device look the same.
      return {
        background: rgba(bg, 0.3),
        color: fg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '0.06em solid rgba(255,255,255,0.35)',
      }
  }
}

function HandIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-[1em] h-[1em] shrink-0" aria-hidden>
      <path d="M8 13V5a2 2 0 0 1 4 0v6" />
      <path d="M12 11V4a2 2 0 0 1 4 0v7" />
      <path d="M16 11V6a2 2 0 0 1 4 0v9a6 6 0 0 1-6 6h-2a6 6 0 0 1-5.2-3l-2.3-4a2 2 0 0 1 3.4-2l1.1 1.8" />
    </svg>
  )
}
function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-[1em] h-[1em] shrink-0" aria-hidden>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  )
}

function CtaButton({
  stopScreen, posClass, onClick,
}: {
  stopScreen: StopScreenManifest
  posClass: string
  onClick: () => void
}) {
  const variant = stopScreen.cta_variant || 'pill'
  const style   = stopScreen.cta_style || 'glassy'
  const size    = stopScreen.cta_size || 'lg'
  const bg      = stopScreen.cta_bg_color || '#FFFFFF'
  const fg      = stopScreen.cta_text_color || '#000000'

  const shapeCls =
    variant === 'button' ? 'rounded-[0.5em]'
    : variant === 'circle' ? 'rounded-full aspect-square justify-center !px-0 min-w-[2.6em]'
    : 'rounded-full' // pill / hand / arrow

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...ctaFillStyle(style, bg, fg),
        fontSize: SIZE_CQMIN[size] ?? SIZE_CQMIN.lg,
      }}
      className={`absolute ${posClass} z-10 inline-flex items-center px-[1em] py-[0.5em] gap-[0.4em] ${shapeCls} font-semibold shadow-2xl`}
    >
      {variant === 'hand' && <HandIcon />}
      {stopScreen.cta_text && <span className="whitespace-nowrap">{stopScreen.cta_text}</span>}
      {variant === 'arrow' && <ArrowIcon />}
    </button>
  )
}
